import get from 'lodash/get';
import isNil from 'lodash/isNil';
import first from 'lodash/first';
import isEmpty from 'lodash/isEmpty';
import {
  SubscriptionLine,
  Amount,
  DiscountAllocation,
  SubscriptionDiscountPercentageValue,
  SubscriptionDiscountFixedAmountValue,
  isPercentageDiscount,
} from './types';

export interface AppliedDiscount {
  title?: string;
  amount: number;
  discountsIds: string[];
  currencyCode?: string;
  usagesRemaining?: number;
  type?: string;
  value?:
    | SubscriptionDiscountPercentageValue
    | SubscriptionDiscountFixedAmountValue;
}

export interface AppliedShippingDiscount {
  id: string;
  title?: string;
  targetType: string;
  recurringCycleLimit?: number;
  usageCount?: number;
  usagesRemaining?: number;
  value?:
    | SubscriptionDiscountPercentageValue
    | SubscriptionDiscountFixedAmountValue;
}

type AppliedShippingNodeDiscount = {
  node: AppliedShippingDiscount;
};

export const totalSpendAmount = (lines: SubscriptionLine[]): Amount => {
  if (lines.length < 1) return { amount: '0', currencyCode: 'USD' };

  const amount = lines.reduce((accum, line) => {
    const quantity = line.quantity;
    const amount = quantity * parseFloat(line.currentPrice.amount);
    return accum + amount;
  }, 0);

  return {
    amount: amount.toString(),
    currencyCode: lines[0].currentPrice.currencyCode,
  };
};

export const appliedDiscounts = function (lines: SubscriptionLine[]): {
  amount: number;
  currencyCode: string;
  discount: DiscountAllocation;
  discountId: string;
  title: string;
  usagesRemaining: number;
}[] {
  const totalsByDiscountId = lines
    .flatMap(({ discountAllocations }) => discountAllocations)
    .filter(Boolean)
    .reduce((acc, discountAllocation) => {
      const { amount, discount } = discountAllocation || {};

      const {
        id: discountId,
        title,
        recurringCycleLimit,
        usageCount,
      } = discount || {};
      let newDiscount;

      if (acc[discountId]) {
        newDiscount = {
          ...acc,
          [discountId]: {
            ...acc[discountId],
            amount:
              parseFloat(acc[discountId].amount) +
              parseFloat(amount?.amount || '0'),
          },
        };
        return newDiscount;
      }
      newDiscount = {
        ...acc,
        [discountId]: {
          discountId,
          amount: parseFloat(amount?.amount || '0'),
          title,
          currencyCode:
            amount?.currencyCode || get(lines[0], 'currentPrice.currencyCode'),
          usagesRemaining: recurringCycleLimit - usageCount,
          discount,
        },
      };
      return newDiscount;
    }, {});

  return Object.keys(totalsByDiscountId).map((key) => totalsByDiscountId[key]);
};

/**
 * Compute the total discount to be applied to a subscription contract by looking
 * at the discounts applied to the different line items.
 *
 * @param lines array of subscription contract lines returned by Shopify's API.
 * @returns `undefined` if no discounts are applied. If discounts are found it will
 *          an object with title, amount and currencyCode.
 *          - Title will only be set if all the discounts have the same title, otherwise
 *          it will be undefined.
 *          - Currency is assumed to be the same for all discounts so the currencyCode
 *          of the price of the first line item is returned.
 */
export const discountToApply = (
  lines: SubscriptionLine[],
): AppliedDiscount | undefined => {
  const titles = new Set<string>();
  const values = new Set<
    SubscriptionDiscountPercentageValue | SubscriptionDiscountFixedAmountValue
  >();
  const usageLimits = new Set<number>();
  const currencyCode = get(lines[0], 'currentPrice.currencyCode');
  const discountsIds = new Set<string>();
  const totalDiscount = lines.reduce((accumulated, line) => {
    const {
      discount,
      titles: lineTitles,
      usageLimits: lineLimits,
      discountIds: lineDiscountIds,
      values: lineValues,
    } = lineDiscount(line);
    lineTitles.forEach(titles.add, titles);
    lineLimits.forEach(usageLimits.add, usageLimits);
    lineDiscountIds.forEach(discountsIds.add, discountsIds);
    lineValues.forEach(values.add, values);
    return accumulated + discount;
  }, 0);

  const totalSpend = parseFloat(totalSpendAmount(lines).amount);
  return totalDiscount > 0
    ? {
        title: valueIfOnlyOneItemExists(titles),
        amount: Math.min(totalDiscount, totalSpend),
        currencyCode,
        type: getDiscountType(lines, [...discountsIds][0]),
        discountsIds: [...discountsIds],
        value: [...values][0],
        usagesRemaining:
          usageLimits.size > 0 ? sorted(usageLimits)[0] : undefined,
      }
    : undefined;
};

const getDiscountType = (lines, discountId) => {
  let discountType;
  lines.forEach(({ discountAllocations }) => {
    const foundDiscount = discountAllocations?.find(
      ({ discount }) => discount.id === discountId,
    );
    if (foundDiscount) {
      discountType = foundDiscount.discount.type;
    }
  });
  return discountType;
};

export const shippingDiscountToApply = (
  shippingDiscounts: AppliedShippingNodeDiscount[],
): AppliedShippingDiscount | undefined => {
  if (!shippingDiscounts) return undefined;

  return first(
    shippingDiscounts.reduce((acc: AppliedShippingDiscount[], { node }) => {
      if (node.targetType === 'SHIPPING_LINE') {
        acc.push({
          ...node,
          usagesRemaining: node.recurringCycleLimit
            ? node.recurringCycleLimit - (node.usageCount || 0)
            : undefined,
        });
      }
      return acc;
    }, []),
  );
};

const valueIfOnlyOneItemExists = <T>(values: Set<T>): T | undefined =>
  values.size === 1 ? values.values().next().value : undefined;

const sorted = <T>(values: Set<T>): T[] => [...values].sort();

interface LineDiscount {
  discount: number;
  titles: string[];
  usageLimits: number[];
  discountIds: string[];
  values: Array<
    SubscriptionDiscountPercentageValue | SubscriptionDiscountFixedAmountValue
  >;
}

const lineDiscount = (line: SubscriptionLine): LineDiscount => {
  const initialValue: LineDiscount = {
    discount: 0,
    titles: [],
    usageLimits: [],
    discountIds: [],
    values: [],
  };
  return (
    line.discountAllocations?.reduce((accumulated, discount) => {
      const {
        discount: totalDiscount,
        titles,
        usageLimits,
        discountIds,
        values,
      } = accumulated;
      const {
        discount: {
          usageCount,
          recurringCycleLimit,
          title: currentTitle,
          id: currentId,
          value,
        },
      } = discount;
      const hasExceedUsageLimit =
        usageCount && recurringCycleLimit && usageCount >= recurringCycleLimit;
      const newDiscount = !hasExceedUsageLimit
        ? totalDiscount + parseFloat(discount.amount.amount)
        : totalDiscount;
      if (
        !hasExceedUsageLimit &&
        !isNil(usageCount) &&
        !isNil(recurringCycleLimit)
      ) {
        // had to force unwrap the optionals in here otherwise the build fails
        // because the compiler cannot ensure the values are defined. Force unwrapping
        // is safe because isNil warranties that they are not null or undefined.
        usageLimits.push(recurringCycleLimit! - usageCount!);
      }
      return {
        discount: newDiscount,
        titles: [...titles, currentTitle],
        usageLimits,
        discountIds: [...discountIds, currentId],
        values: [...values, value],
      };
    }, initialValue) || initialValue
  );
};

/**
 * Recompute the discounts applied to each line item after "something has changed".
 * Designed to be used after the user edits the line items of a subscription contract
 * by adding/removing line items or by updating the quantities of the products in a
 * line item.
 *
 * @param lines array of subscription contract lines.
 * @returns received lines with the updates
 */
export const updateDiscountsAmount = (
  lines: SubscriptionLine[],
): SubscriptionLine[] => {
  const { amount } = totalSpendAmount(lines);
  const totalSpend = parseFloat(amount);
  return lines.map((line) => {
    const newDiscounts = updateLineDiscounts(line, totalSpend);
    return {
      ...line,
      discountAllocations: newDiscounts,
    };
  });
};

const updateLineDiscounts = (
  line: SubscriptionLine,
  totalSpend: number,
): DiscountAllocation[] => {
  const isDynamic = !!(
    line?.customAttributes?.find(({ key }) => key === '_bundleType')?.value ===
    'dynamicBaB'
  );

  const discountAllocations = isDynamic
    ? line.discountAllocations.reduce((acc, discount) => {
        if (isPercentageDiscount(discount?.discount.value)) {
          return [...acc, discount];
        }
        if (acc.find((d) => d.discount.id === discount.discount.id)) {
          return acc;
        }
        return [...acc, discount];
      }, [])
    : line.discountAllocations;

  return (
    discountAllocations?.map((discount) => {
      const {
        currentPrice: { amount },
        quantity,
      } = line;
      const lineItemPrice = parseFloat(amount);
      const newDiscount = isPercentageDiscount(discount?.discount.value)
        ? calculatePercentageDiscount(lineItemPrice, quantity, discount)
        : calculateFixedAmountDiscount(
            totalSpend,
            lineItemPrice,
            quantity,
            discount,
          );

      return newDiscount;
    }) || []
  );
};

const calculatePercentageDiscount = (
  price: number,
  quantity: number,
  discount: DiscountAllocation,
): DiscountAllocation => {
  const { percentage } = discount?.discount
    .value as SubscriptionDiscountPercentageValue;
  const newAmount = (price * quantity * percentage) / 100;
  return {
    ...discount,
    amount: {
      ...discount.amount,
      amount: newAmount.toString(),
    },
  };
};

const calculateFixedAmountDiscount = (
  totalSpend: number,
  price: number,
  quantity: number,
  discount: DiscountAllocation,
): DiscountAllocation => {
  const totalAmountToDiscount = parseFloat(
    (discount.discount.value as SubscriptionDiscountFixedAmountValue).amount
      .amount,
  );
  const totalPrice = price * quantity;
  const percentageOfTotal = totalPrice / totalSpend;
  return {
    ...discount,
    amount: {
      ...discount.amount,
      amount: Math.min(
        percentageOfTotal * totalAmountToDiscount,
        totalPrice,
      ).toString(),
    },
  };
};

export const getCycleDiscount = (
  pricingPolicyCycleDiscount?: PricingPolicyCycleDiscount[],
  successfulBillingAttempts?: string[],
): PricingPolicyCycleDiscount | undefined => {
  if (!pricingPolicyCycleDiscount || isEmpty(pricingPolicyCycleDiscount)) {
    return;
  }

  const cycles = successfulBillingAttempts?.length || 0;

  let discount;

  for (const d of pricingPolicyCycleDiscount) {
    if (d.afterCycle >= (discount?.afterCycle || 0) && d.afterCycle <= cycles) {
      discount = d;
    }
  }

  return discount || pricingPolicyCycleDiscount[0];
};
